import { useHistory } from 'react-router-dom'
import { usePostHog } from 'posthog-js/react'

import { useAppDispatch } from '../../../../store/hooks'
import { setAuthState } from '../../../../store/auth-state-slice'
import { AuthStateEnum } from '@vacationtracker/shared/types/auth-state'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { ICheckUserId } from '@vacationtracker/shared/types/company'

import { FrontendUrls } from '../../../../types/urls'
// The contract belongs to the page being navigated to, so it is stated once, there.
import type { ISigninAccountHandover } from '../../../signin/new-signin/types'
import { ConnectPlatform } from '../types'

export interface ISaveCreateUserParams {
  userData: ICheckUserId
  platform: PlatformEnum
  extra: Record<string, unknown>
}

export interface ISignupNavigation {
  goToPersonalInfo: (email: string, withEmailInvites?: boolean) => void
  saveCreateUser: (params: ISaveCreateUserParams) => void
  goToCompanyDetails: () => void
  goToAllSignupOptions: () => void
  goToLogin: (state?: string) => void
  goToLoginWithAccount: (handover: ISigninAccountHandover) => void
  goToLoginWithPlatform: (platform: ConnectPlatform) => void
}

/**
 * The whole contract with the rest of the sign-up flow. The later steps are rendered by the
 * old container on their own routes, and they rehydrate from localStorage on mount, so what
 * is written here and where the visitor is pushed is all that carries across.
 */
export const useSignupNavigation = (): ISignupNavigation => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const posthog = usePostHog()

  const startRecordingAndEnterSignup = () => {
    if (process.env.REACT_APP_ENVIRONMENT === 'production') {
      posthog.startSessionRecording()
    }
    dispatch(setAuthState(AuthStateEnum.createCompany))
  }

  return {
    goToPersonalInfo: (email: string, withEmailInvites = false) => {
      startRecordingAndEnterSignup()
      localStorage.setItem(
        'vtCreateUser',
        JSON.stringify({
          platform: PlatformEnum.Email,
          mail: email,
          userEmail: email,
        })
      )
      if (withEmailInvites) {
        localStorage.setItem('vtEmailInvites', JSON.stringify([]))
      }
      history.push(`${FrontendUrls.signup}${FrontendUrls.personalInfo}`)
    },

    saveCreateUser: ({ userData, platform, extra }: ISaveCreateUserParams) => {
      localStorage.setItem(
        'vtCreateUser',
        JSON.stringify({
          name: userData.name,
          id: `${platform}-${userData.id}`,
          mail: userData.mail,
          userEmail: userData.mail,
          platform,
          ...extra,
        })
      )
    },

    goToCompanyDetails: () => {
      startRecordingAndEnterSignup()
      history.push(`${FrontendUrls.signup}${FrontendUrls.companyDetails}`)
    },

    // Dropping the whole query string is safe: the plan and the affiliate code were written to
    // storage when the page was opened, and this page is the only reader of the rest.
    goToAllSignupOptions: () => {
      history.push(FrontendUrls.signup)
    },

    goToLogin: (state?: string) => {
      dispatch(setAuthState(AuthStateEnum.signIn))
      history.push(FrontendUrls.signin + (state ? `?${state}=true` : ''))
    },

    // The lookup rides along in the history entry rather than the query string: it is an
    // answer about someone's account, not something to leave in a URL that gets shared,
    // logged and reported.
    goToLoginWithAccount: (handover: ISigninAccountHandover) => {
      dispatch(setAuthState(AuthStateEnum.signIn))
      history.push(FrontendUrls.signin, { signinAccount: handover })
    },

    // Opened on that platform's button. Someone not added yet has no account for the address to
    // find, and the sign-in page answers that with "sign up", which leads straight back here.
    goToLoginWithPlatform: (platform: ConnectPlatform) => {
      dispatch(setAuthState(AuthStateEnum.signIn))
      history.push(`${FrontendUrls.signin}?platform=${platform}`)
    },
  }
}
